import { useCallback, useEffect, useState } from "react";
import api, { euro } from "../lib/api";
import { Wallet, TrendingUp, CreditCard, Dumbbell, Receipt } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Cassa() {
  const [period, setPeriod] = useState("oggi");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);

  const range = useCallback(() => {
    const today = new Date();
    let from = iso(today), to = iso(today);
    if (period === "settimana") { const s = new Date(today); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); from = iso(s); }
    if (period === "mese") from = iso(new Date(today.getFullYear(), today.getMonth(), 1));
    if (period === "anno") from = iso(new Date(today.getFullYear(), 0, 1));
    if (period === "custom") { from = customFrom || from; to = customTo || to; }
    return { from, to };
  }, [period, customFrom, customTo]);

  const load = useCallback(async () => {
    const { from, to } = range();
    try {
      const { data } = await api.get("/billing/summary", { params: { date_from: from, date_to: to } });
      setData(data);
    } catch { /* noop */ }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ll:data-changed", h);
    return () => window.removeEventListener("ll:data-changed", h);
  }, [load]);

  const Card = ({ icon: Icon, label, value, color, tid }) => (
    <div className="kpi-card bg-white rounded-2xl border border-slate-100 p-5" data-testid={tid}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-widest text-slate-400">{label}</p>
        <span className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center text-white`}><Icon size={17} /></span>
      </div>
      <p className="font-heading text-3xl font-bold mt-2">{value}</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="cassa-page">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-2xl font-bold">Cassa</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="flex bg-white rounded-full border border-slate-200 p-1" data-testid="cassa-period">
            {[["oggi", "Oggi"], ["settimana", "Settimana"], ["mese", "Mese"], ["anno", "Anno"], ["custom", "Periodo"]].map(([v, l]) => (
              <button key={v} data-testid={`cassa-period-${v}`} onClick={() => setPeriod(v)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${period === v ? "bg-blue-600 text-white" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          {period === "custom" && <>
            <input data-testid="cassa-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" />
            <input data-testid="cassa-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" />
          </>}
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="cassa-kpis">
            <Card tid="cassa-entrate-pazienti" icon={CreditCard} label="Entrate pazienti" value={euro(data.entrate_pazienti)} color="bg-blue-600" />
            <Card tid="cassa-entrate-palestra" icon={Dumbbell} label="Entrate palestra" value={euro(data.entrate_palestra)} color="bg-cyan-500" />
            <Card tid="cassa-entrate-totali" icon={TrendingUp} label="Entrate totali" value={euro(data.entrate_totali)} color="bg-emerald-500" />
            <Card tid="cassa-spese" icon={Receipt} label="Spese" value={euro(data.spese)} color="bg-amber-500" />
            <Card tid="cassa-saldo" icon={Wallet} label="Saldo" value={euro(data.saldo)} color={data.saldo >= 0 ? "bg-emerald-600" : "bg-red-500"} />
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-5" data-testid="cassa-chart">
              <h3 className="font-heading font-bold mb-4">Andamento giornaliero</h3>
              {data.series.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">Nessun movimento nel periodo</p> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(8, 10) + "/" + d.slice(5, 7)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => euro(v)} />
                    <Legend />
                    <Bar dataKey="entrate" name="Entrate" fill="#2563EB" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="uscite" name="Uscite" fill="#F59E0B" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="space-y-5">
              <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="cassa-by-method">
                <h3 className="font-heading font-bold mb-3">Entrate per metodo</h3>
                {Object.entries(data.by_method).map(([m, v]) => (
                  <div key={m} className="flex justify-between py-1.5 text-sm border-b border-slate-50 last:border-0">
                    <span className="capitalize text-slate-600">{m}</span><span className="font-semibold">{euro(v)}</span>
                  </div>
                ))}
                {Object.keys(data.by_method).length === 0 && <p className="text-sm text-slate-400">Nessuna entrata</p>}
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="cassa-by-category">
                <h3 className="font-heading font-bold mb-3">Spese per categoria</h3>
                {Object.entries(data.by_category).map(([c, v]) => (
                  <div key={c} className="flex justify-between py-1.5 text-sm border-b border-slate-50 last:border-0">
                    <span className="capitalize text-slate-600">{c}</span><span className="font-semibold text-amber-600">{euro(v)}</span>
                  </div>
                ))}
                {Object.keys(data.by_category).length === 0 && <p className="text-sm text-slate-400">Nessuna spesa</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
